/**
 * OkBotChatWidget — Floating AI assistant chat button + panel.
 *
 * Appears for all logged-in users when OkBot is enabled.
 * Sends messages via POST /api/okbot/message (user-facing proxy).
 * Passes rich platform context with each message so OkBot can answer
 * questions about categories, listings, orders, etc.
 */
import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Strip JSON blobs from OkBot responses.
 * OkBot sometimes wraps its text in a JSON envelope that leaks through.
 * This extracts a clean text string.
 */
function sanitizeOkBotText(raw) {
    if (!raw || typeof raw !== 'string') return String(raw || '');
    const trimmed = raw.trim();
    // If it looks like a JSON object/array try to extract the text field
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
            const parsed = JSON.parse(trimmed);
            if (typeof parsed === 'object' && parsed !== null) {
                // Pick the first text-like field from common names
                const text =
                    parsed.response || parsed.reply || parsed.message ||
                    parsed.text || parsed.answer || parsed.content ||
                    parsed.data?.response || parsed.data?.reply ||
                    parsed.data?.text || parsed.data?.summary ||
                    parsed.summary || null;
                if (text && typeof text === 'string') return sanitizeOkBotText(text);
            }
        } catch { /* not valid JSON — fall through */ }
    }
    // Strip markdown code fences that wrap JSON
    const fenceMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```$/m);
    if (fenceMatch) return sanitizeOkBotText(fenceMatch[1].trim());
    return trimmed;
}
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Bot, X, Send, Minimize2, Maximize2, Sparkles, RefreshCw } from 'lucide-react';

const WELCOME_MSG = {
    role: 'bot',
    text: '¡Hola! Soy OkBot, tu asistente de inteligencia artificial en OkLoVendo 🤖\n\nPuedo ayudarte con información sobre anuncios, categorías, ventas, compras, envíos y cualquier duda sobre la plataforma. ¿En qué te puedo ayudar?',
    ts: Date.now(),
};

export default function OkBotChatWidget() {
    const { user, role } = useAuth();

    const [enabled, setEnabled] = useState(false);
    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [messages, setMessages] = useState([WELCOME_MSG]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [sessionId] = useState(() => `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    // Check if OkBot is enabled (public status endpoint)
    useEffect(() => {
        if (role !== 'user') return;
        api.get('/okbot/status')
            .then(r => setEnabled(!!r.data?.enabled))
            .catch(() => setEnabled(false));
    }, [role]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (open && !minimized) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, open, minimized]);

    // Focus input when opening
    useEffect(() => {
        if (open && !minimized) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open, minimized]);

    const buildContext = useCallback(() => {
        return {
            platform: 'oklovendo',
            platform_name: 'Ok te lo vendo',
            platform_url: window.location.origin,
            current_path: window.location.pathname,
            user_id: user?.id,
            user_nickname: user?.nickname,
        };
    }, [user]);

    const sendMessage = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending) return;

        setInput('');
        setMessages(prev => [...prev, { role: 'user', text, ts: Date.now() }]);
        setSending(true);

        try {
            const { data } = await api.post('/okbot/message', {
                message: text,
                session_id: sessionId,
                context: buildContext(),
            });
            const raw =
                data?.reply ||
                data?.message ||
                data?.response ||
                data?.text ||
                data?.answer ||
                (typeof data === 'string' ? data : null) ||
                'Recibí tu mensaje, pero no pude procesar la respuesta.';
            const reply = sanitizeOkBotText(raw);
            setMessages(prev => [...prev, { role: 'bot', text: reply, ts: Date.now() }]);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'No pude conectar con OkBot en este momento. Intenta de nuevo.';
            setMessages(prev => [...prev, { role: 'error', text: detail, ts: Date.now() }]);
        } finally {
            setSending(false);
        }
    };

    const reset = () => {
        setMessages([WELCOME_MSG]);
        setInput('');
    };

    // Don't render if not a logged-in user or OkBot not enabled
    if (role !== 'user' || !enabled) return null;

    const ACCENT = '#E53935';

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    title="Asistente OkBot"
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 9000,
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: ACCENT,
                        color: '#fff',
                        border: 'none',
                        boxShadow: '0 4px 24px rgba(229,57,53,0.45)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 30px rgba(229,57,53,0.55)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(229,57,53,0.45)'; }}
                >
                    <Bot size={26} />
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 9000,
                        width: 360,
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: 16,
                        boxShadow: '0 8px 48px rgba(0,0,0,0.22)',
                        background: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: 'height 0.2s',
                        height: minimized ? 56 : 520,
                        maxHeight: 'calc(100vh - 48px)',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            background: ACCENT,
                            color: '#fff',
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexShrink: 0,
                            cursor: 'pointer',
                        }}
                        onClick={() => setMinimized(v => !v)}
                    >
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Bot size={18} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>OkBot</div>
                            <div style={{ fontSize: 11, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4CAF50', display: 'inline-block' }} />
                                Asistente IA · En línea
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <button
                                onClick={e => { e.stopPropagation(); reset(); }}
                                title="Nueva conversación"
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                <RefreshCw size={13} />
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setMinimized(v => !v); }}
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                {minimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setOpen(false); setMinimized(false); }}
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                <X size={13} />
                            </button>
                        </div>
                    </div>

                    {/* Messages area */}
                    {!minimized && (
                        <>
                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', display: 'flex', flexDirection: 'column', gap: 8, background: '#f9fafb' }}>
                                {messages.map((m, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                        {m.role !== 'user' && (
                                            <div style={{ width: 26, height: 26, borderRadius: '50%', background: m.role === 'error' ? '#fef2f2' : ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 6, marginTop: 2 }}>
                                                {m.role === 'error'
                                                    ? <span style={{ fontSize: 13 }}>⚠️</span>
                                                    : <Bot size={13} color="#fff" />
                                                }
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                maxWidth: '78%',
                                                padding: '8px 11px',
                                                borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                                background: m.role === 'user'
                                                    ? ACCENT
                                                    : m.role === 'error'
                                                    ? '#fef2f2'
                                                    : '#ffffff',
                                                color: m.role === 'user'
                                                    ? '#fff'
                                                    : m.role === 'error'
                                                    ? '#b91c1c'
                                                    : '#111827',
                                                fontSize: 13,
                                                lineHeight: 1.45,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                boxShadow: m.role !== 'user' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                                border: m.role === 'error' ? '1px solid #fecaca' : 'none',
                                            }}
                                        >
                                            {m.text}
                                        </div>
                                    </div>
                                ))}

                                {/* Typing indicator */}
                                {sending && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Bot size={13} color="#fff" />
                                        </div>
                                        <div style={{ background: '#fff', borderRadius: '14px 14px 14px 4px', padding: '8px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', gap: 4, alignItems: 'center' }}>
                                            {[0, 1, 2].map(i => (
                                                <span key={i} style={{
                                                    width: 6, height: 6, borderRadius: '50%', background: '#9ca3af',
                                                    display: 'inline-block',
                                                    animation: `bounce 1.2s ${i * 0.2}s infinite`,
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div ref={bottomRef} />
                            </div>

                            {/* Quick suggestions */}
                            {messages.length <= 1 && (
                                <div style={{ padding: '6px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', background: '#f9fafb', borderTop: '1px solid #f3f4f6' }}>
                                    {['¿Cómo vendo?', '¿Cómo compro?', '¿Cómo funciona el envío?', '¿Cómo me registro?'].map(q => (
                                        <button
                                            key={q}
                                            onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                                            style={{
                                                background: '#fff',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 20,
                                                padding: '4px 10px',
                                                fontSize: 11,
                                                cursor: 'pointer',
                                                color: '#374151',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Input */}
                            <form
                                onSubmit={sendMessage}
                                style={{ padding: '10px 10px 12px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, background: '#fff' }}
                            >
                                <input
                                    ref={inputRef}
                                    value={input}
                                    onChange={e => setInput(e.target.value)}
                                    disabled={sending}
                                    placeholder="Escribe tu pregunta…"
                                    style={{
                                        flex: 1,
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 10,
                                        padding: '8px 12px',
                                        fontSize: 13,
                                        outline: 'none',
                                        background: '#f9fafb',
                                    }}
                                    onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.background = '#fff'; }}
                                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }}
                                />
                                <button
                                    type="submit"
                                    disabled={!input.trim() || sending}
                                    style={{
                                        background: input.trim() && !sending ? ACCENT : '#e5e7eb',
                                        border: 'none',
                                        borderRadius: 10,
                                        color: input.trim() && !sending ? '#fff' : '#9ca3af',
                                        width: 38,
                                        height: 38,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: input.trim() && !sending ? 'pointer' : 'default',
                                        transition: 'background 0.15s',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Send size={15} />
                                </button>
                            </form>

                            {/* Footer */}
                            <div style={{ padding: '4px 12px 8px', textAlign: 'center', fontSize: 10, color: '#9ca3af', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                <Sparkles size={10} />
                                Powered by OkBot · OkGo Ecosystem
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Bounce keyframes */}
            <style>{`
                @keyframes bounce {
                    0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
                    40% { transform: translateY(-4px); opacity: 1; }
                }
            `}</style>
        </>
    );
}
